'use client';

import { ClearButton, Element } from '@vuddy/components';
import {
  SurfaceColorToken,
  theme,
  TypoColorToken,
} from '@vuddy/components/theme';
import { isNonNil } from '@vuddy/utils';
import { motion } from 'framer-motion';
import { isNil } from 'lodash-es';
import Link from 'next/link';
import { ReactNode } from 'react';
import { useTabsContext } from './useTabsContext';

interface TabTitleProps {
  isActive: boolean;
}

interface TabProps {
  index: number;
  title: ReactNode;
  children?: ReactNode;
  targetId?: string;
  onSelect?: (index: number) => void | false;
  paddingY?: number;
  activeBorderWidth?: number;
  inactiveBorderWidth?: number;
  backgroundColor?: SurfaceColorToken;
  activeColor?: TypoColorToken;
  inActiveTextColor?: TypoColorToken;
}

export const Tab = ({
  index,
  title,
  targetId,
  onSelect,
  backgroundColor = 'primary',
  activeColor = 'primary',
  inActiveTextColor = 'tertiary',
}: TabProps) => {
  const { activeIndex, activeTabStyle, inactiveTabStyle, changeActiveIndex } =
    useTabsContext();
  const isActive = activeIndex === index;

  const handleClick = () => {
    if (onSelect?.(index) === false) return;
    changeActiveIndex(index);
  };

  if (isNonNil(targetId)) {
    return (
      <Element width="100%" backgroundColor={backgroundColor}>
        <Link
          href={`#${targetId}`}
          onClick={e => {
            e.preventDefault();
            if (onSelect?.(index) === false) return;
            const target = document.querySelector(`#${targetId}`);

            if (isNil(target)) return;

            changeActiveIndex(index);
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }}>
          <motion.div
            initial={isActive ? 'active' : 'inactive'}
            animate={isActive ? 'active' : 'inactive'}
            variants={{
              active: {
                color: theme.text[activeColor],
              },
              inactive: {
                color: theme.text[inActiveTextColor],
              },
            }}>
            <Element
              paddingY="padding_xs"
              textAlign="center"
              typography={isActive ? 'bodySmallAccent' : 'bodySmall'}
              className={isActive ? activeTabStyle : inactiveTabStyle}>
              {title}
            </Element>
          </motion.div>
        </Link>
      </Element>
    );
  }

  return (
    <ClearButton onClick={handleClick} role="tab" fullWidth>
      <motion.div
        initial={isActive ? 'active' : 'inactive'}
        animate={isActive ? 'active' : 'inactive'}
        variants={{
          active: {
            color: theme.text.primary,
          },
          inactive: {
            color: theme.text.tertiary,
          },
        }}>
        <Element
          paddingY="padding_xs"
          textAlign="center"
          backgroundColor="primary"
          typography={isActive ? 'bodySmallAccent' : 'bodySmall'}>
          {title}
        </Element>
      </motion.div>
    </ClearButton>
  );
};
